/* Oddsaint theme scripts. No dependencies. */
(function () {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const parser = new DOMParser();

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const parseHTML = (html) => parser.parseFromString(html, 'text/html');

  function debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  /* ------------------------------------------------------------------
     Dialogs (drawers, modals)
     ------------------------------------------------------------------ */
  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    dialog.classList.remove('is-closing');
    dialog.showModal();
    const autofocus = $('[data-predictive-search-input]', dialog);
    if (autofocus) autofocus.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      dialog.close();
      return;
    }
    dialog.classList.add('is-closing');
    const finish = () => {
      dialog.classList.remove('is-closing');
      dialog.close();
    };
    setTimeout(finish, 320);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        openDialog(dialog);
      }
      return;
    }

    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }

    // Click on the backdrop (the dialog element itself, outside its panel).
    if (event.target.tagName === 'DIALOG' && event.target.open) {
      closeDialog(event.target);
    }
  });

  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.tagName === 'DIALOG') {
        event.preventDefault();
        closeDialog(event.target);
      }
    },
    true
  );

  // Close drawers that were opened on mobile when the layout switches to desktop.
  window.matchMedia('(min-width: 990px)').addEventListener('change', (mq) => {
    if (!mq.matches) return;
    ['MenuDrawer', 'FilterDrawer'].forEach((id) => {
      const dialog = document.getElementById(id);
      if (dialog && dialog.open) dialog.close();
    });
  });

  /* ------------------------------------------------------------------
     Header
     ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('[data-header]');
    if (!header || !header.hasAttribute('data-transparent')) return;

    const firstSection = $('#MainContent > .shopify-section');
    const startsWithHero = firstSection && firstSection.querySelector('.hero');
    const wrapper = header.closest('.shopify-section');

    if (!startsWithHero) {
      header.classList.add('is-solid');
      if (wrapper) wrapper.style.marginBottom = '0';
      return;
    }

    const update = () => header.classList.toggle('is-solid', window.scrollY > 40);
    update();
    if (header.dataset.scrollInit) return;
    header.dataset.scrollInit = 'true';
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------------------------------------------
     Announcement bar rotator
     ------------------------------------------------------------------ */
  function initRotators(root = document) {
    $$('[data-rotator]', root).forEach((bar) => {
      if (bar.dataset.rotatorInit) return;
      bar.dataset.rotatorInit = 'true';
      const messages = $$('.announcement-bar__message', bar);
      if (messages.length < 2) return;
      let index = 0;
      let paused = false;
      bar.addEventListener('mouseenter', () => (paused = true));
      bar.addEventListener('mouseleave', () => (paused = false));
      setInterval(() => {
        if (paused || document.hidden) return;
        messages[index].classList.remove('is-active');
        messages[index].setAttribute('aria-hidden', 'true');
        index = (index + 1) % messages.length;
        messages[index].classList.add('is-active');
        messages[index].removeAttribute('aria-hidden');
      }, Number(bar.dataset.interval) || 5000);
    });
  }

  /* ------------------------------------------------------------------
     Sliders
     ------------------------------------------------------------------ */
  function initSliders(root = document) {
    $$('[data-slider]', root).forEach((slider) => {
      const track = $('[data-slider-track]', slider);
      const prev = $('[data-slider-prev]', slider);
      const next = $('[data-slider-next]', slider);
      if (!track || slider.dataset.sliderInit) return;
      slider.dataset.sliderInit = 'true';

      const step = () => {
        const item = track.firstElementChild;
        if (!item) return track.clientWidth;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const perView = Math.max(1, Math.floor((track.clientWidth + gap) / (item.offsetWidth + gap)));
        return perView * (item.offsetWidth + gap);
      };

      const updateButtons = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(updateButtons, 60), { passive: true });
      window.addEventListener('resize', debounce(updateButtons, 150));
      updateButtons();
    });
  }

  /* ------------------------------------------------------------------
     Quantity inputs (product page, cart page)
     ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-change]');
    if (!button) return;
    const wrapper = button.closest('[data-quantity]');
    const input = wrapper && $('input', wrapper);
    if (!input) return;
    const min = Number(input.min) || 0;
    const value = Math.max(min, (Number(input.value) || 0) + Number(button.dataset.quantityChange));
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     Auto-submitting selects & checkboxes (filters, sort, localization)
     ------------------------------------------------------------------ */
  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-autosubmit]');
    if (!field || !field.form) return;
    if (typeof field.form.requestSubmit === 'function') {
      field.form.requestSubmit();
    } else {
      field.form.submit();
    }
  });

  /* ------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------ */
  const cartDrawer = () => document.getElementById('CartDrawer');

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.classList.toggle('is-empty', Number(count) === 0);
    });
  }

  function renderCartDrawer(sections) {
    const html = sections && sections['cart-drawer'];
    const current = $('[data-cart-drawer-content]');
    if (!html || !current) return;
    const fresh = $('[data-cart-drawer-content]', parseHTML(html));
    if (!fresh) return;
    current.innerHTML = fresh.innerHTML;
    current.dataset.itemCount = fresh.dataset.itemCount;
    updateCartCount(fresh.dataset.itemCount);
  }

  async function refreshCartDrawer() {
    const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
    renderCartDrawer(await response.json());
  }

  async function changeCartLine(line, quantity) {
    const content = $('[data-cart-drawer-content]');
    if (content) content.classList.add('is-loading');
    try {
      const response = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: Math.max(0, Number(quantity)),
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (data.status || data.errors) {
        await refreshCartDrawer();
        const error = $('[data-cart-error]');
        if (error) {
          error.textContent = data.description || data.errors || theme.strings.cartError;
          error.hidden = false;
        }
        return;
      }

      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }
      renderCartDrawer(data.sections);
    } catch (error) {
      console.error(error);
    } finally {
      const fresh = $('[data-cart-drawer-content]');
      if (fresh) fresh.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', (event) => {
    const change = event.target.closest('[data-cart-change]');
    if (change) {
      event.preventDefault();
      changeCartLine(change.dataset.line, change.dataset.quantity);
      return;
    }

    const cartLink = event.target.closest('[data-open-cart]');
    if (cartLink && cartDrawer() && !document.body.classList.contains('template-cart')) {
      event.preventDefault();
      openDialog(cartDrawer());
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (input) changeCartLine(input.dataset.line, input.value);
  });

  async function addToCart(form) {
    const button = $('[data-add-to-cart]', form);
    const buttonText = $('[data-add-to-cart-text]', form);
    const error = $('[data-form-error]', form);
    const originalText = buttonText ? buttonText.textContent : '';

    if (error) error.hidden = true;
    if (button) {
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
    }
    if (buttonText) buttonText.textContent = theme.strings.adding;

    const body = new FormData(form);
    body.append('sections', 'cart-drawer');
    body.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();

      if (data.status) {
        if (error) {
          error.textContent = data.description || data.message || theme.strings.cartError;
          error.hidden = false;
        }
        return;
      }

      if (theme.cartType === 'page' || !cartDrawer()) {
        window.location.href = theme.routes.cartUrl;
        return;
      }

      renderCartDrawer(data.sections);
      openDialog(cartDrawer());
    } catch (err) {
      console.error(err);
      if (error) {
        error.textContent = theme.strings.cartError;
        error.hidden = false;
      }
    } finally {
      if (button) {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
      if (buttonText) buttonText.textContent = originalText;
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  /* ------------------------------------------------------------------
     Product page: variants & gallery
     ------------------------------------------------------------------ */
  function getSelectedOptions(picker) {
    return $$('fieldset', picker).map((fieldset) => {
      const checked = $('input:checked', fieldset);
      return checked ? checked.value : null;
    });
  }

  function setBuyButton(section, variant) {
    const button = $('[data-add-to-cart]', section);
    const text = $('[data-add-to-cart-text]', section);
    if (!button || !text) return;
    if (!variant) {
      button.disabled = true;
      text.textContent = theme.strings.unavailable;
    } else {
      button.disabled = !variant.available;
      text.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
    }
  }

  function showVariantMedia(section, variant) {
    if (!variant || !variant.featured_media) return;
    const track = $('[data-gallery-track]', section);
    const item = track && $(`[data-media-id="${variant.featured_media.id}"]`, track);
    if (!item) return;
    if (window.matchMedia('(min-width: 990px)').matches) {
      track.prepend(item);
    } else {
      track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
    }
  }

  let variantRequest = 0;

  async function onVariantChange(section, picker) {
    const variantsEl = $('[data-variants-json]', picker);
    if (!variantsEl) return;
    const variants = JSON.parse(variantsEl.textContent);
    const selected = getSelectedOptions(picker);

    $$('fieldset', picker).forEach((fieldset, i) => {
      const label = $('[data-selected-value]', fieldset);
      if (label) label.textContent = selected[i] || '';
    });

    const variant = variants.find((v) => v.options.every((option, i) => option === selected[i]));
    setBuyButton(section, variant);
    if (!variant) return;

    const idInput = $('[data-variant-id]', section);
    if (idInput) idInput.value = variant.id;
    showVariantMedia(section, variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Re-render price, stock and option availability from the server.
    const requestId = ++variantRequest;
    try {
      const response = await fetch(`${section.dataset.productUrl}?variant=${variant.id}&section_id=${section.dataset.sectionId}`);
      const html = await response.text();
      if (requestId !== variantRequest) return;
      const doc = parseHTML(html);
      ['[data-price-container]', '[data-inventory]'].forEach((selector) => {
        const fresh = $(selector, doc);
        const current = $(selector, section);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      });
      const freshPicker = $('[data-variant-picker]', doc);
      if (freshPicker) {
        const activeId = document.activeElement && document.activeElement.id;
        picker.innerHTML = freshPicker.innerHTML;
        if (activeId) {
          const toFocus = document.getElementById(activeId);
          if (toFocus) toFocus.focus({ preventScroll: true });
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  document.addEventListener('change', (event) => {
    const picker = event.target.closest('[data-variant-picker]');
    if (!picker) return;
    const section = picker.closest('[data-product-section]');
    if (section) onVariantChange(section, picker);
  });

  function initGalleries(root = document) {
    $$('[data-gallery]', root).forEach((gallery) => {
      const track = $('[data-gallery-track]', gallery);
      const dots = $$('.product__gallery-dot', gallery);
      if (!track || !dots.length || gallery.dataset.galleryInit) return;
      gallery.dataset.galleryInit = 'true';
      track.addEventListener(
        'scroll',
        debounce(() => {
          const index = Math.round(track.scrollLeft / track.clientWidth);
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        }, 50),
        { passive: true }
      );
    });
  }

  /* Size guide unit toggle */
  document.addEventListener('click', (event) => {
    const unitButton = event.target.closest('[data-unit]');
    if (!unitButton) return;
    const guide = unitButton.closest('.size-guide');
    const unit = unitButton.dataset.unit;
    $$('[data-unit]', guide).forEach((btn) => {
      const active = btn === unitButton;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
    $$('[data-in]', guide).forEach((el) => (el.hidden = unit !== 'in'));
    $$('[data-cm]', guide).forEach((el) => (el.hidden = unit !== 'cm'));
  });

  /* ------------------------------------------------------------------
     Product recommendations
     ------------------------------------------------------------------ */
  function initRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach(async (container) => {
      if (container.dataset.loaded || !container.dataset.url) return;
      container.dataset.loaded = 'true';
      try {
        const response = await fetch(container.dataset.url);
        const doc = parseHTML(await response.text());
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) container.innerHTML = fresh.innerHTML;
      } catch (error) {
        console.error(error);
      }
    });
  }

  /* ------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------ */
  function initPredictiveSearch() {
    const input = $('[data-predictive-search-input]');
    const results = $('[data-predictive-search-results]');
    if (!input || !results || !theme.routes.predictiveSearch) return;
    let controller;

    input.addEventListener(
      'input',
      debounce(async () => {
        const terms = input.value.trim();
        if (controller) controller.abort();
        if (!terms) {
          results.innerHTML = '';
          return;
        }
        controller = new AbortController();
        const params = new URLSearchParams({
          q: terms,
          'resources[type]': 'product,collection,page',
          'resources[limit]': '6',
          section_id: 'predictive-search',
        });
        try {
          const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: controller.signal });
          if (!response.ok) return;
          const fresh = $('#PredictiveSearchResults', parseHTML(await response.text()));
          results.innerHTML = fresh ? fresh.outerHTML : '';
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }, 250)
    );
  }

  /* ------------------------------------------------------------------
     Customer
     ------------------------------------------------------------------ */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-confirm]');
    if (form && !window.confirm(form.dataset.confirm)) event.preventDefault();
  });

  document.addEventListener('DOMContentLoaded', () => {
    $$('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  });

  /* ------------------------------------------------------------------
     Init
     ------------------------------------------------------------------ */
  function initSection(root) {
    initRotators(root);
    initSliders(root);
    initGalleries(root);
    initRecommendations(root);
  }

  function init() {
    initHeader();
    initSection(document);
    initPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', (event) => {
    initSection(event.target);
    if (event.target.querySelector('[data-header]')) initHeader();
  });
})();
